import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Lock, AlertTriangle, Play, Loader2 } from "lucide-react";

const REASON_HUMAN = {
  auto_daily_cap: "Today's spending hit the daily cap.",
  manual: "Manually paused.",
  dryrun_completed_re_armed: "Re-armed after a dry-run.",
};

function humanReason(raw) {
  if (!raw) return "Pipeline is paused.";
  if (REASON_HUMAN[raw]) return REASON_HUMAN[raw];
  if (raw.startsWith("auto_rearm_hard_pause_") && raw.endsWith("_idle")) {
    const m = raw.match(/_(\d+)m_/);
    const mins = m ? parseInt(m[1], 10) : null;
    if (mins) {
      const hr = Math.round(mins / 60);
      return `Auto-paused after ${hr}h of inactivity.`;
    }
    return "Auto-paused after extended inactivity.";
  }
  if (raw.startsWith("dryrun:")) return `Dry-run pause (${raw}).`;
  if (raw.startsWith("regen:")) return `Paused during regen (${raw}).`;
  return raw;
}

/**
 * KillswitchBanner — sticky, undismissable banner shown whenever the
 * pipeline is paused. Polls /api/system/freeze-status every 10 s so it
 * auto-clears when the killswitch lifts elsewhere.
 *
 * Renders nothing when the pipeline is healthy.
 */
export default function KillswitchBanner() {
  const [freeze, setFreeze] = useState(null);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    const tick = () => {
      api
        .get("/system/freeze-status")
        .then((r) => {
          if (active) setFreeze(r.data || {});
        })
        .catch(() => {
          if (active) setFreeze(null);
        });
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const paused = !!(freeze && freeze.killswitch_paused);
  const hardPause = !!(freeze && freeze.pipeline_hard_pause);
  if (!paused && !hardPause) return null;

  const onResume = async () => {
    setErr(null);
    setLoading(true);
    try {
      await api.post("/system/killswitch", {
        paused: false,
        reason: "manual_resume_via_banner",
      });
      // Force an immediate refresh so the banner disappears without waiting
      // for the 10 s tick.
      const r = await api.get("/system/freeze-status");
      setFreeze(r.data || {});
      setResumeOpen(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setErr(
        status
          ? `Server error ${status}: ${
              typeof detail === "string" ? detail : JSON.stringify(detail)
            }`
          : `Network error: ${e?.message || "unknown"}`,
      );
    }
    setLoading(false);
  };

  return (
    <>
      <div
        data-testid="killswitch-banner"
        className="sticky top-0 z-50 flex flex-wrap items-center gap-3 border-b border-rose-300 bg-rose-50 px-4 py-2 text-sm text-rose-900 shadow-sm"
      >
        <Lock className="h-4 w-4 shrink-0" />
        <div className="flex-1 min-w-[12rem]">
          <span className="font-semibold">Pipeline paused.</span>{" "}
          <span data-testid="killswitch-reason">
            {humanReason(freeze?.reason)}
          </span>{" "}
          {hardPause ? (
            <span
              data-testid="killswitch-hard-pause"
              className="ml-2 rounded border border-rose-300 bg-rose-100 px-1.5 py-0.5 text-[11px] font-medium"
            >
              hard env override — operator must clear PIPELINE_HARD_PAUSE
            </span>
          ) : null}
        </div>
        {!hardPause ? (
          <Button
            size="sm"
            data-testid="killswitch-resume-btn"
            onClick={() => setResumeOpen(true)}
            className="bg-rose-700 text-white hover:bg-rose-800"
          >
            <Play className="mr-1 h-3 w-3" /> Resume pipeline
          </Button>
        ) : null}
      </div>

      <AlertDialog
        open={resumeOpen}
        onOpenChange={(o) => !loading && setResumeOpen(o)}
      >
        <AlertDialogContent data-testid="killswitch-resume-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Play className="h-4 w-4 text-emerald-700" /> Resume pipeline?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-stone-700">
                <p>
                  Lift the soft killswitch and resume the pipeline. New
                  generations and regenerations will be allowed and may
                  spend money on Suno, fal.ai and ElevenLabs again.
                </p>
                <p className="text-xs text-stone-500">
                  Current pause reason:{" "}
                  <code>{freeze?.reason || "(none)"}</code>
                </p>
                {err ? (
                  <div
                    data-testid="killswitch-resume-error"
                    className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900"
                  >
                    {err}
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="killswitch-resume-confirm"
              disabled={loading}
              onClick={(e) => {
                e.preventDefault();
                onResume();
              }}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Resuming…
                </>
              ) : (
                <>
                  <AlertTriangle className="mr-2 h-3 w-3" /> Resume
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
